import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  /** Rendered instead of the tree when a descendant throws. */
  fallback: (reset: () => void) => ReactNode
}

interface State {
  hasError: boolean
}

/**
 * Catches render-time throws so one broken route cannot white-screen the whole
 * site. Without this, any exception in a lazily-loaded page left the visitor
 * looking at an empty document with no way back and nothing logged.
 *
 * Class component by necessity: React exposes no hook equivalent.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Kept as console output on purpose — there is no telemetry sink in this
    // project yet, and swallowing the error entirely would make the fallback
    // impossible to diagnose from a user's bug report.
    console.error('Unhandled render error:', error, info.componentStack)
  }

  reset = () => this.setState({ hasError: false })

  render() {
    if (this.state.hasError) return this.props.fallback(this.reset)
    return this.props.children
  }
}
